import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StarRating from './StarRating';
import { MessageSquare, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang || 'sk', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return '';
  }
};

const ProductReviews = ({ productId }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, token } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [ratingAvg, setRatingAvg] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [myReview, setMyReview] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formRating, setFormRating] = useState(0);
  const [formComment, setFormComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | 'pending'

  useEffect(() => {
    fetchReviews();
    if (isAuthenticated) fetchMyReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isAuthenticated]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/${productId}/reviews`);
      setReviews(res.data.items || []);
      setRatingAvg(res.data.rating_avg || 0);
      setRatingCount(res.data.rating_count || 0);
    } catch (e) {
      // Silent — reviews block degrades gracefully
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReview = async () => {
    try {
      const res = await axios.get(`${API}/products/${productId}/reviews/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyReview(res.data.review || null);
    } catch (e) {
      // ignore
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formRating) return;
    setSubmitting(true);
    setSubmitStatus(null);
    try {
      await axios.post(
        `${API}/products/${productId}/reviews`,
        { rating: formRating, comment: formComment, language: i18n.language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitStatus('pending');
      setFormRating(0);
      setFormComment('');
      // Refresh "my review" — will appear in list after admin approval
      fetchMyReview();
    } catch (err) {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  // Distribution per rating (1..5)
  const distribution = [5, 4, 3, 2, 1].map((n) => {
    const count = reviews.filter((r) => r.rating === n).length;
    const pct = ratingCount > 0 ? Math.round((count / ratingCount) * 100) : 0;
    return { n, count, pct };
  });

  return (
    <div className="border-t border-gray-200 p-8" data-testid="product-reviews-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <MessageSquare className="h-6 w-6 text-orange-500" />
          {t('reviews.title')}
        </h2>
        {ratingCount > 0 && (
          <div className="flex items-center gap-2" data-testid="reviews-summary">
            <StarRating value={ratingAvg} showValue size="h-5 w-5" />
            <span className="text-sm text-gray-500">
              {t('reviews.based_on', { count: ratingCount })}
            </span>
          </div>
        )}
      </div>

      {/* Distribution bars */}
      {ratingCount > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="space-y-2">
            {distribution.map(({ n, count, pct }) => (
              <div key={n} className="flex items-center gap-3 text-sm">
                <span className="w-3 text-gray-700 font-medium">{n}</span>
                <StarRating value={1} size="h-3 w-3" testId={`dist-${n}`} />
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-400 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-10 text-right text-gray-500">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Submit form */}
      {isAuthenticated ? (
        myReview ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-8" data-testid="my-review-status">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-blue-900">
                  {myReview.status === 'approved'
                    ? t('reviews.your_review_approved')
                    : myReview.status === 'rejected'
                    ? t('reviews.your_review_rejected')
                    : t('reviews.your_review_pending')}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <StarRating value={myReview.rating} size="h-4 w-4" />
                </div>
                {myReview.comment && (
                  <p className="mt-2 text-sm text-gray-700 italic">"{myReview.comment}"</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-8"
            data-testid="review-form"
          >
            <h3 className="font-bold text-lg mb-3">{t('reviews.write_review')}</h3>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">
                {t('reviews.your_rating')} *
              </label>
              <StarRating
                value={formRating}
                onChange={setFormRating}
                size="h-8 w-8"
                testId="form-rating"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2" htmlFor="review-comment">
                {t('reviews.your_comment')}
              </label>
              <textarea
                id="review-comment"
                value={formComment}
                onChange={(e) => setFormComment(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder={t('reviews.comment_placeholder')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                data-testid="review-comment-input"
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formComment.length} / 2000
              </div>
            </div>
            {submitStatus === 'error' && (
              <div className="text-red-600 text-sm mb-3" data-testid="review-error">
                {t('reviews.submit_error')}
              </div>
            )}
            <button
              type="submit"
              disabled={!formRating || submitting}
              className="px-6 py-2.5 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              data-testid="review-submit-btn"
            >
              {submitting ? t('reviews.submitting') : t('reviews.submit_review')}
            </button>
            <p className="text-xs text-gray-500 mt-3">
              {t('reviews.moderation_note')}
            </p>
          </form>
        )
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-8 text-center" data-testid="reviews-login-prompt">
          <p className="mb-3 text-gray-700">{t('reviews.login_to_review')}</p>
          <Link
            to="/login"
            className="inline-block px-6 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition"
          >
            {t('reviews.login_btn')}
          </Link>
        </div>
      )}

      {/* Review list */}
      {loading ? (
        <div className="text-gray-500 text-center py-8">{t('common.loading')}</div>
      ) : reviews.length === 0 ? (
        <div className="text-gray-500 text-center py-8" data-testid="reviews-empty">
          {t('reviews.no_reviews_yet')}
        </div>
      ) : (
        <div className="space-y-4" data-testid="reviews-list">
          {reviews.map((r) => (
            <div
              key={r.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition"
              data-testid={`review-${r.id}`}
            >
              <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                    {(r.user_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold">{r.user_name || 'Anonymous'}</div>
                    <div className="text-xs text-gray-500">{formatDate(r.created_at, i18n.language)}</div>
                  </div>
                </div>
                <StarRating value={r.rating} size="h-4 w-4" />
              </div>
              {r.comment && (
                <p className="text-gray-700 mt-2 whitespace-pre-wrap">{r.comment}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
